import React, { FC, ReactElement, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import PageWithHeader from '@app/components/PageWithHeader';
import Loader from '@app/components/Loader';
import useLoadable from '@app/hooks/loadable/useLoadable';
import validationSystemServices from '@mortee/services/validationSystemServices';
import { SupplierValidationDocument, transformSupplierValidationDocument } from '@mortee/domain/validationSystem';
import { extractLogErrorIdFromError, HttpStatus, isRequestError, RequestError } from '@app/libs/request';

interface RouteParams {
  staticId: string;
  documentId: string;
}

interface ViewerData {
  document: SupplierValidationDocument;
  content: ArrayBuffer;
}

type ViewableContentKind = 'pdf' | 'image' | 'unsupported';

// Explicit allowlist, not a "not an image, so it must be safe to iframe" fallback: the backend's Tika
// scan only allows pdf/jpeg/png through today, but that guarantee lives in a different module's config
// - this page must not assume it and render anything else straight into an iframe.
function classifyContentType(contentType: string): ViewableContentKind {
  if (contentType === 'application/pdf') {
    return 'pdf';
  }

  if (contentType.startsWith('image/')) {
    return 'image';
  }

  return 'unsupported';
}

const DOCUMENT_NOT_FOUND_ERROR: RequestError = {
  code: HttpStatus.notFound,
  message: 'Document not found.',
  responseJSON: null,
  logSupportId: undefined,
  logCorrelationId: undefined,
};

const ValidationRecordDocumentViewerPage: FC = observer(() => {
  const { staticId, documentId } = useParams<RouteParams>();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  const [viewerDataLoadable] = useLoadable<ViewerData>(async () => {
    const [documentsResponse, content] = await Promise.all([
      validationSystemServices.getValidationRecordDocuments(staticId),
      validationSystemServices.getValidationRecordDocumentContent(staticId, documentId),
    ]);

    const document = documentsResponse.map(transformSupplierValidationDocument).find((candidate) => candidate.id === documentId);

    if (!document) {
      throw DOCUMENT_NOT_FOUND_ERROR;
    }

    return { document, content };
  }, [staticId, documentId]);

  useEffect(() => {
    if (!viewerDataLoadable.isResolved()) {
      return undefined;
    }

    const { document, content } = viewerDataLoadable.result;
    const url = URL.createObjectURL(new Blob([content], { type: document.contentType }));
    setBlobUrl(url);

    return (): void => {
      URL.revokeObjectURL(url);
    };
  }, [viewerDataLoadable]);

  function renderError(): ReactElement {
    const error = viewerDataLoadable.isRejected() ? viewerDataLoadable.stateMetadata?.error : undefined;

    if (isRequestError(error) && error.code === HttpStatus.notFound) {
      return <ErrorMessage data-testid='document-viewer-error'>Document not found.</ErrorMessage>;
    }

    if (isRequestError(error) && error.code === HttpStatus.forbidden) {
      return <ErrorMessage data-testid='document-viewer-error'>You do not have access to this document.</ErrorMessage>;
    }

    return (
      <ErrorMessage data-testid='document-viewer-error'>
        Error loading document, refer to error code: {extractLogErrorIdFromError(error)}
      </ErrorMessage>
    );
  }

  function renderContent(): ReactElement {
    if (viewerDataLoadable.isRejected()) {
      return renderError();
    }

    if (!viewerDataLoadable.isResolved() || !blobUrl) {
      return <Loader spinning transparent />;
    }

    const { document } = viewerDataLoadable.result;
    const contentKind = classifyContentType(document.contentType);

    return (
      <>
        <Title data-testid='document-viewer-title'>{document.originalFileName}</Title>
        {contentKind === 'image' && (
          <ViewerImage data-testid='document-viewer-image' src={blobUrl} alt={document.originalFileName} />
        )}
        {contentKind === 'pdf' && (
          <ViewerFrame data-testid='document-viewer-pdf-frame' src={blobUrl} title={document.originalFileName} />
        )}
        {contentKind === 'unsupported' && (
          <ErrorMessage data-testid='document-viewer-unsupported-type'>This document type cannot be previewed.</ErrorMessage>
        )}
      </>
    );
  }

  return (
    <PageWithHeader width='fullWithoutPadding' paddingTop='none'>
      <Container data-testid='document-viewer-page'>{renderContent()}</Container>
    </PageWithHeader>
  );
});

export default ValidationRecordDocumentViewerPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: calc(100vh - 120px);
  padding: 20px;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const ViewerFrame = styled.iframe`
  flex: 1;
  width: 100%;
  border: 1px solid var(--transparent-gray-150);
`;

const ViewerImage = styled.img`
  flex: 1;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  align-self: center;
`;

const ErrorMessage = styled.div`
  padding: 20px;
`;
